import React, { useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { useGetHintCoverageMatrix } from '~/hooks/apis/connection-google/useHintCoverageMatrix';

import { HintCoverageMatrixCell, HintCoverageMatrixParams } from '~/services/ConnectionGoogleService';

import TierTotalsLegend from './TierTotalsLegend';
import { formatPct, getCellColor } from './utils';

const ABW_PAGE_SIZE = 30;
const LOS_PAGE_SIZE = 10;
const MAX_ABW = 300;
const MAX_LOS = 30;

const stickyColumnSx = { position: 'sticky', left: 0, zIndex: 1, bgcolor: 'grey.100', whiteSpace: 'nowrap' } as const;
const dataCellSx = { whiteSpace: 'nowrap' } as const;

interface Props {
  accountType: HintCoverageMatrixParams['accountType'];
  windowHours: number;
}

export default function HintCoverageDetailedMatrix({ accountType, windowHours }: Props) {
  const theme = useTheme();
  const [abwPage, setAbwPage] = useState(0);
  const [losPage, setLosPage] = useState(0);

  const minAbw = abwPage * ABW_PAGE_SIZE + 1;
  const maxAbw = Math.min(minAbw + ABW_PAGE_SIZE - 1, MAX_ABW);
  const minLos = losPage * LOS_PAGE_SIZE + 1;
  const maxLos = Math.min(minLos + LOS_PAGE_SIZE - 1, MAX_LOS);

  const { data, isLoading, error } = useGetHintCoverageMatrix({
    accountType,
    windowHours,
    minAbw,
    maxAbw,
    minLos,
    maxLos,
  });

  const { abwValues, losValues, cellMap } = useMemo(() => {
    if (!data?.matrix) {
      return { abwValues: [], losValues: [], cellMap: new Map<string, HintCoverageMatrixCell>() };
    }

    const abwSet = new Set<number>();
    const losSet = new Set<number>();
    const map = new Map<string, HintCoverageMatrixCell>();

    for (const cell of data.matrix) {
      abwSet.add(cell.abw);
      losSet.add(cell.los);
      map.set(`${cell.abw}-${cell.los}`, cell);
    }

    return {
      abwValues: Array.from(abwSet).sort((a, b) => a - b),
      losValues: Array.from(losSet).sort((a, b) => a - b),
      cellMap: map,
    };
  }, [data]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error.message || 'Failed to load hint coverage data'}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Box mt={2}>
      <TierTotalsLegend tierTotals={data.tierTotals} />

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader sx={{ minWidth: abwValues.length * 90 }}>
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{ ...stickyColumnSx, zIndex: 3, bgcolor: 'grey.200', minWidth: theme.spacing(7.5) }}
              >
                <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                  ABW →
                </Typography>
                <br />
                <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                  LOS ↓
                </Typography>
              </TableCell>
              {abwValues.map((abw) => (
                <TableCell key={abw} align="center" sx={{ minWidth: theme.spacing(10) }}>
                  {abw}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {losValues.map((los) => (
              <TableRow key={los}>
                <TableCell align="center" sx={stickyColumnSx}>
                  <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                    {los}
                  </Typography>
                </TableCell>
                {abwValues.map((abw) => {
                  const cell = cellMap.get(`${abw}-${los}`);
                  if (!cell) {
                    return (
                      <TableCell key={abw} align="center" sx={{ ...dataCellSx, bgcolor: 'grey.50' }}>
                        —
                      </TableCell>
                    );
                  }

                  return (
                    <Tooltip
                      key={abw}
                      title={
                        <Box>
                          <Typography variant="caption" display="block">
                            ABW: {abw}, LOS: {los}
                          </Typography>
                          <Typography variant="caption" display="block">
                            HIGH: {cell.high.served} served ({formatPct(cell.high.pct)})
                          </Typography>
                          <Typography variant="caption" display="block">
                            MEDIUM: {cell.medium.served} served ({formatPct(cell.medium.pct)})
                          </Typography>
                          <Typography variant="caption" display="block">
                            LOW: {cell.low.served} served ({formatPct(cell.low.pct)})
                          </Typography>
                          {cell.lastServedAt && (
                            <Typography variant="caption" display="block">
                              Last served: {new Date(cell.lastServedAt).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                    >
                      <TableCell
                        align="center"
                        sx={{ ...dataCellSx, bgcolor: getCellColor(theme, cell), cursor: 'default' }}
                      >
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: 'success.main', fontWeight: theme.typography.fontWeightMedium }}
                        >
                          H: {formatPct(cell.high.pct)}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: 'warning.main' }}>
                          M: {formatPct(cell.medium.pct)}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: 'error.main' }}>
                          L: {formatPct(cell.low.pct)}
                        </Typography>
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2" color="text.secondary">
          Showing ABW {minAbw}-{maxAbw}, LOS {minLos}-{maxLos}
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" disabled={abwPage === 0} onClick={() => setAbwPage((p) => p - 1)}>
            ← Prev ABW
          </Button>
          <Button size="small" variant="outlined" disabled={maxAbw >= MAX_ABW} onClick={() => setAbwPage((p) => p + 1)}>
            Next ABW →
          </Button>
          <Button size="small" variant="outlined" disabled={losPage === 0} onClick={() => setLosPage((p) => p - 1)}>
            ↑ Prev LOS
          </Button>
          <Button size="small" variant="outlined" disabled={maxLos >= MAX_LOS} onClick={() => setLosPage((p) => p + 1)}>
            Next LOS ↓
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
